import {
    Box,
    Checkbox,
    FormControlLabel,
    Grid,
    TableBody,
    TableCell,
    TableRow,
    Theme,
    Tooltip,
    Typography,
    useTheme
} from "@mui/material";
import {
    ColorMap,
    MutualFundTableBodyProps,
    SchemeInfoProps,
    TableBodyData,
    TableItemCheckboxProps
} from "../../types";
import {
    CheckIcon,
    UnCheckIcon
} from "../../../../assets/SvgIcons";
import { useState } from "react";
import { checkImageExists } from "../../../../utils/utilityFunctions";
import { ROLE_GROUP,SUBROLE } from "../../../../utils/constant";
import { useSelector } from "react-redux";

const colorMap: ColorMap = {
    low: 'success.500',
    medium: 'warning.500',
    "very high": 'error.500'
}

function SchemeInfo({
    company,
    schemeName,
    type,
    subType,
    risk,
    isinCode,
    onSchemeClick = () => { return; }
}: SchemeInfoProps) {
    const amcIds = ['BDOTR', 'BPIAMC']
    const checkAmcId = amcIds.includes(company ?? '') ? company?.toLowerCase() : company;
    return (
        <Box sx={{ display: "flex", alignItems: "center", flexDirection: 'row', gap: '8px' }}>
            {company &&
                <img
                    src={checkImageExists(`${checkAmcId}`) ? `../../../../../../images/amcIcons/${checkAmcId}.svg` : `../../../../../../images/amcIcons/default.svg`}
                    alt={company}
                    width={40}
                    height={40}
                    style={{
                        objectFit: "contain",
                        borderRadius: "8px"
                    }}
                />
            }
            <Box
                sx={{
                    cursor: "pointer",
                    width: '100%',
                }}
                onClick={onSchemeClick}
            >
                <Tooltip title={schemeName} followCursor>
                    <Typography
                        variant="subtitle_500"
                        sx={(theme: Theme) => ({
                            color: theme.palette.text.primary,
                            '&:hover': {
                                color: theme.palette.primary[500],
                            },
                        })}
                    >
                        {schemeName}
                    </Typography>
                </Tooltip>
                <Grid
                    container
                    sx={{
                        marginTop: "10px",
                        flexWrap: "nowrap",
                        alignItems: "center",
                        columnGap: "8px"
                    }}
                >
                    <Grid item sx={{ whiteSpace: "nowrap" }}>
                        <Typography
                            variant="subtitle_600"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.secondary,
                            })}
                        >
                            {type}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Box
                            sx={{
                                width: "3px",
                                height: "3px",
                                backgroundColor: "text.secondary",
                                borderRadius: "50%",
                            }}
                        />
                    </Grid>
                    <Grid item sx={{ whiteSpace: "nowrap" }}>
                        <Typography
                            variant="subtitle_600"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.secondary,
                                width: '100%',
                            })}
                        >
                            {subType}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Box
                            sx={(theme: Theme) => ({
                                width: "3px",
                                height: "3px",
                                backgroundColor: theme.palette.common.black,
                                borderRadius: "50%",
                                display: (!risk || risk === "NULL") ? "none" : "block",
                            })}
                        />
                    </Grid>
                    <Grid item sx={{ whiteSpace: "nowrap" }}>
                        <Typography
                            variant="subtitle_600"
                            sx={(theme: Theme) => ({
                                color: theme.palette.common.black,
                                display: (!risk || risk === "NULL") ? "none" : "block",
                            })}
                        >
                            {risk} Risk
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Box
                            sx={(theme: Theme) => ({
                                width: "3px",
                                height: "3px",
                                backgroundColor: theme.palette.common.black,
                                borderRadius: "50%",
                                display: (!isinCode || isinCode === "NULL") ? "none" : "block",
                            })}
                        />
                    </Grid>
                    <Grid item sx={{ whiteSpace: "nowrap" }}>
                        <Typography
                            variant="subtitle_600"
                            sx={(theme: Theme) => ({
                                color: theme.palette.common.black,
                                display: (!isinCode || isinCode === "NULL") ? "none" : "block",
                            })}
                        >
                            {isinCode && (`(${isinCode})`)}
                        </Typography>
                    </Grid>
                </Grid>

            </Box >
        </Box>
    )
}

function TableItemCheckbox({
    company,
    value,
    schemeName,
    type = "",
    subType = "",
    risk = "",
    checked,
    isinCode = "",
    onCheckboxClick,
    onSchemeClick = () => { return; }
}: TableItemCheckboxProps) {
    return (
        <FormControlLabel
            value={value}
            control={
                <Checkbox
                    size="small"
                    checkedIcon={<CheckIcon />}
                    icon={<UnCheckIcon />}
                    sx={{
                        '&:hover': {
                            backgroundColor: 'transparent',
                        },
                    }}
                    onClick={(event) => event.stopPropagation()}
                    onChange={onCheckboxClick}
                    checked={checked}
                />
            }
            label={
                <SchemeInfo
                    company={company}
                    schemeName={schemeName}
                    type={type}
                    subType={subType}
                    risk={risk}
                    isinCode={isinCode}
                />
            }
            sx={{
                display: 'flex',
                "& .MuiFormControlLabel-label": {
                    marginLeft: "15px",
                    width: "100%",
                }
            }}
            onClick={(event) => {
                event.preventDefault()
                onSchemeClick()
            }}
        />
    );
}


export default function MutualFundTableBody(
    {
        tableBodyData,
        tableBodyRowCustomStyles = {},
        showCheckbox = true,
        handleCheckboxClick = () => { return; },
        handleSchemeClick = () => { return; },
    }: MutualFundTableBodyProps
): JSX.Element {
    const theme = useTheme()
    const { roleGroup  } = useSelector((state: any) => state.auth);
    const showCheckBox = showCheckbox &&(![ROLE_GROUP.RSP].includes(roleGroup))

    return (
        <>
            {tableBodyData.map((obj: TableBodyData) => (
                <TableRow
                    key={obj.scheme}
                    sx={{
                        '& td': {
                            paddingTop: "8px",
                            paddingBottom: "8px",
                            borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                        },
                        ...tableBodyRowCustomStyles
                    }}
                >
                    <TableCell
                        align="left"
                    >
                        {showCheckBox ?
                            <TableItemCheckbox
                                company={obj?.company ?? ""}
                                schemeName={obj?.schemeName ?? ""}
                                value={obj?.scheme ?? ""}
                                type={obj?.categoryName ?? ""}
                                subType={obj?.subCategoryName ?? ""}
                                risk={obj?.riskType ?? ""}
                                checked={!!obj?.checked}
                                isinCode={obj?.isinCode}
                                onCheckboxClick={(event: React.ChangeEvent<HTMLInputElement>) => handleCheckboxClick(obj?.scheme ?? "", event.target.checked)}
                                onSchemeClick={() => handleSchemeClick(obj)}
                            /> : <SchemeInfo
                                company={obj?.company ?? ""}
                                schemeName={obj?.schemeName ?? ""}
                                type={obj?.categoryName ?? ""}
                                subType={obj?.subCategoryName ?? ""}
                                risk={obj?.riskType ?? ""}
                                isinCode={obj?.isinCode}
                                onSchemeClick={() => handleSchemeClick(obj)}
                            />}
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="subtitle_600"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.year1Return && !Number.isNaN(Number(obj.year1Return)) ? `${Number(obj.year1Return).toFixed(2)}%` : "0%"}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="subtitle_600"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.year3Return && !Number.isNaN(Number(obj.year3Return)) ? `${Number(obj.year3Return).toFixed(2)}%` : "0%"}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="subtitle_600"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.year5Return && !Number.isNaN(Number(obj.year5Return)) ? `${Number(obj.year5Return).toFixed(2)}%` : "0%"}
                        </Typography>
                    </TableCell>
                </TableRow>
            ))}
        </>
    )
}

export function MutualFundResponsiveBody({
    tableBodyData,
    tableBodyRowCustomStyles = {},
    showCheckbox = true,
    handleCheckboxClick = () => { return; },
    handleSchemeClick = () => { return; },
    tableHeaderData = [],
}: MutualFundTableBodyProps): JSX.Element {
    const { roleGroup  } = useSelector((state: any) => state.auth)
    const { palette } = useTheme()
    const [tableHeaderKey, setTableHeaderKey] = useState<string>(tableHeaderData[1].key || tableHeaderData[1].id)

    const style: any = { color: palette.text.secondary, fontWeight: 500, fontSize: '14px', textTransform: 'uppercase', textWrap: 'nowrap', paddingRight: 0, paddingLeft: 0 }
    const showCheckBox = showCheckbox && (![ROLE_GROUP.RSP].includes(roleGroup))
    return (
        <TableBody>
            {tableBodyData.map((item: any, index: number) => (
                <TableRow key={index}>
                    <TableCell
                        style={{ ...style }}
                        align='left'
                    >
                        <Typography
                            variant="subtitle_500"
                            sx={(theme: Theme) => ({
                                // color: theme.palette.primary[500],
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                maxWidth: '200px',
                                width: '100%',
                            })}
                        >
                            {showCheckBox ?
                                <TableItemCheckbox
                                    schemeName={item?.schemeName ?? ""}
                                    value={item?.scheme ?? ""}
                                    type={item?.categoryName ?? ""}
                                    subType={item?.subCategoryName ?? ""}
                                    risk={item?.riskType ?? ""}
                                    checked={!!item?.checked}
                                    onCheckboxClick={(event: React.ChangeEvent<HTMLInputElement>) => handleCheckboxClick(item?.scheme ?? "", event.target.checked)}
                                    onSchemeClick={() => handleSchemeClick(item)}
                                /> : <SchemeInfo
                                    company={item?.company ?? ""}
                                    schemeName={item?.schemeName ?? ""}
                                    type={item?.categoryName ?? ""}
                                    subType={item?.subCategoryName ?? ""}
                                    risk={item?.riskType ?? ""}
                                    isinCode={item?.isinCode}
                                    onSchemeClick={() => handleSchemeClick(item)}
                                />}
                            {/* {item?.[tableHeaderData[0].id]} */}
                        </Typography>
                    </TableCell>
                    <TableCell
                        style={{ ...style }}
                        align='center'
                    >
                        <Typography
                            variant="subtitle_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary,
                            })}
                        >
                            {tableHeaderKey.includes('.') ? item?.[tableHeaderKey.split('.')[0]][tableHeaderKey.split('.')[1]] : item?.[tableHeaderKey]}
                        </Typography>
                    </TableCell>
                </TableRow>
            ))}
        </TableBody>
    )
}